var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useRef, useState } from 'react';
import Manager from '../modal/Manager';
import Suggestions from './Suggestions';
import Tag from './Tag';
import { useLanguage } from '@base/LanguageProvider';
import { TagType } from '@common/api';
import { useTagContext } from '@data/tag/tagContext';
import { isAccountTag } from '@data/tag/types';
import { TagAddButtonAndLabel } from '@styled/Basic';
import { FaPlus } from 'react-icons/fa';
import { getTagInternalTypeName, getTagTypeName } from '../../data/tag/tagUtil';
export var Keys = {
    ENTER: 13,
    TAB: 9,
    BACKSPACE: 8,
    UP_ARROW: 38,
    DOWN_ARROW: 40,
    ESCAPE: 27,
};
export var DefaultClassNames = {
    tags: 'tags',
    tagInput: 'tag-input',
    selected: 'tag-selected',
    tag: 'tag',
    remove: 'tag-remove',
    suggestions: 'tag-suggestions',
    suggestionsItem: 'tag-suggestions-item',
    tagListBtn: 'tag-list-button',
};
function Tags(_a) {
    var placeholder = _a.placeholder, _b = _a.tags, tags = _b === void 0 ? [] : _b, disabled = _a.disabled, _c = _a.canUpdate, canUpdate = _c === void 0 ? true : _c, create = _a.create, select = _a.select, remove = _a.remove, classNames = _a.classNames, _d = _a.singleLine, singleLine = _d === void 0 ? false : _d, customIcon = _a.customIcon, initialParentTag = _a.initialParentTag, excludeInternalTags = _a.excludeInternalTags, _e = _a.hideTags, hideTags = _e === void 0 ? false : _e, customButton = _a.customButton, _f = _a.style, style = _f === void 0 ? {} : _f, _g = _a.tiny, tiny = _g === void 0 ? false : _g, filterByTypes = _a.filterByTypes, filterByInternalTypes = _a.filterByInternalTypes, filterByParentIds = _a.filterByParentIds, _h = _a.autoEdit, autoEdit = _h === void 0 ? false : _h, inheritedTags = _a.inheritedTags, _j = _a.hideAddButton, hideAddButton = _j === void 0 ? false : _j, _k = _a.canAddOnlyOneTag, canAddOnlyOneTag = _k === void 0 ? false : _k, afterButtonLabel = _a.afterButtonLabel, _l = _a.afterButtonLabelType, afterButtonLabelType = _l === void 0 ? TagType.Table : _l;
    var suggestionsContainerRef = useRef(null);
    var _m = __read(useState(autoEdit), 2), showAddNewTag = _m[0], setShowAddNewTag = _m[1];
    var _o = __read(useState(-1), 2), selectedIndex = _o[0], setSelectedIndex = _o[1];
    var _p = __read(useState(false), 2), showingSuggestions = _p[0], setShowingSuggestions = _p[1];
    var _q = __read(useState(''), 2), query = _q[0], setQuery = _q[1];
    var _r = __read(useState(false), 2), selectionMode = _r[0], setSelectionMode = _r[1];
    var _s = __read(useState(initialParentTag || null), 2), parentTag = _s[0], setParentTag = _s[1];
    var L = useLanguage().L;
    var classes = __assign(__assign({}, DefaultClassNames), classNames);
    var tagData = useTagContext().tagData;
    var filteredSuggestions = useMemo(function () {
        if (!showingSuggestions) {
            return [];
        }
        var ids = query.length > 0 || filterByTypes || filterByParentIds || filterByInternalTypes
            ? tagData.searchTagsAndReturnIds(query, filterByTypes, filterByInternalTypes, filterByParentIds)
            : undefined;
        var parent = tagData.tagsByParentId[parentTag ? parentTag.id : 'null'];
        if (parent) {
            return tagData.createTagList(parent, 0, {
                searchTagIds: ids,
                disabledTagIds: new Set(tags.map(function (tag) { return tag.id; })),
                excludeInternalTags: excludeInternalTags,
            });
        }
        else {
            return [];
        }
    }, [query, tagData, parentTag, tags, showingSuggestions]);
    var addTagRef = useRef(null);
    var resetState = function () {
        setShowAddNewTag(false);
        setSelectedIndex(-1);
        setShowingSuggestions(false);
        setQuery('');
        setSelectionMode(false);
        setParentTag(null);
    };
    var showHelper = function () {
        Manager.renderTitle('top', addTagRef.current, L(placeholderString));
    };
    var onFocusHandler = function () {
        setShowingSuggestions(true);
    };
    var removeTag = function (tagId) {
        if (remove) {
            resetState();
            remove(tagId);
            Manager.closePopover();
        }
    };
    var selectTag = function (tagId) {
        if (select) {
            resetState();
            select(tagId);
            Manager.closePopover();
        }
    };
    var createTag = function (tagName) {
        if (create) {
            resetState();
            create(tagName);
            Manager.closePopover();
        }
    };
    var handleChange = function (e) {
        setQuery(e.target.value);
    };
    var showTagInput = function () {
        Manager.closePopover();
        setShowAddNewTag(true);
    };
    var handleKeyDown = function (e) {
        if (e.keyCode === Keys.ESCAPE) {
            setSelectedIndex(-1);
            setSelectionMode(false);
            return;
        }
        if (e.keyCode === Keys.ENTER || e.keyCode === Keys.TAB) {
            if (e.keyCode !== Keys.TAB || query !== '') {
                e.preventDefault();
            }
            if (query !== '' && !query.includes('"')) {
                if (selectionMode) {
                    if (filteredSuggestions[selectedIndex]) {
                        selectTag(filteredSuggestions[selectedIndex].id);
                        return;
                    }
                }
                var find = filteredSuggestions.find(function (sug) { return sug.name.toLowerCase() === query.toLowerCase(); });
                if (find) {
                    selectTag(find.id);
                    return;
                }
                createTag(query);
                return;
            }
        }
        // up arrow
        if (e.keyCode === Keys.UP_ARROW) {
            e.preventDefault();
            // last item, cycle to the top
            if (selectedIndex <= 0) {
                setSelectedIndex(filteredSuggestions.length - 1);
                setSelectionMode(true);
            }
            else {
                setSelectedIndex(selectedIndex - 1);
                setSelectionMode(true);
            }
            return;
        }
        // down arrow
        if (e.keyCode === Keys.DOWN_ARROW) {
            e.preventDefault();
            setSelectedIndex((selectedIndex + 1) % filteredSuggestions.length);
            setSelectionMode(true);
            return;
        }
    };
    var handleSuggestionHover = function (i) {
        setSelectedIndex(i);
        setSelectionMode(true);
    };
    var handleSuggestionClick = function (i) {
        selectTag(filteredSuggestions[i].id);
    };
    var handleParentTagClick = function (i) {
        setParentTag(filteredSuggestions[i].tag);
        setQuery('');
    };
    var handleParentTagBackClick = function () {
        setParentTag(null);
    };
    var getTagComponent = function (tag, info, idx, parentName) {
        var isInherited = (inheritedTags === null || inheritedTags === void 0 ? void 0 : inheritedTags.find(function (t) { return t.id === (tag === null || tag === void 0 ? void 0 : tag.id); })) !== undefined ? true : false;
        if (tag && info && info.inherited) {
            return (React.createElement(Tag, { key: "inh-".concat(tag.id, "-").concat(idx), tag: tag, canUpdate: false, parentName: parentName, tiny: tiny, isInherited: isInherited }));
        }
        else if (tag) {
            return (React.createElement(Tag, { key: "tag-".concat(tag.id, "-").concat(idx), tag: tag, canUpdate: canUpdate, onDelete: function () { return removeTag(tag.id); }, parentName: parentName, tiny: tiny, isInherited: isInherited }));
        }
        else {
            return (React.createElement("div", { style: { paddingRight: '5px' }, key: 'null' + idx }, "REF. LOST"));
        }
    };
    var renderTags = function (tags) {
        var tagsComponents = [];
        var sortedTags = __spreadArray([], __read(tags), false).sort(function (tagA, tagB) {
            var tagRepA = tagData.tagById[tagA.id];
            var tagRepB = tagData.tagById[tagB.id];
            if (tagRepA && isAccountTag(tagRepA) && tagRepB && isAccountTag(tagRepB)) {
                return 0;
            }
            if (tagRepA && isAccountTag(tagRepA)) {
                return -1;
            }
            return 1;
        });
        sortedTags.forEach(function (tagInfo, idx) {
            var _a;
            var tag = tagData.tagById[tagInfo.id];
            var parentName = ((tag === null || tag === void 0 ? void 0 : tag.ParentId) && ((_a = tagData.tagById[tag.ParentId]) === null || _a === void 0 ? void 0 : _a.name)) || '';
            var info = 'inherited' in tagInfo ? tagInfo : undefined;
            tagsComponents.push(getTagComponent(tag, info, idx, parentName));
        });
        return tagsComponents;
    };
    var placeholderString = placeholder
        ? placeholder
        : 'Add New ' +
            (filterByInternalTypes && filterByInternalTypes.length > 0
                ? filterByInternalTypes.map(function (internalType) { return getTagInternalTypeName({ internalType: internalType }); }).join('/')
                : filterByTypes && filterByTypes.length > 0
                    ? filterByTypes.map(function (type) { return getTagTypeName({ type: type }); }).join('/')
                    : 'Variable/Table/Dimension/Group');
    return (React.createElement("div", { className: "".concat(classes.tags).concat(singleLine ? '' : ' multiline'), style: style },
        React.createElement("div", { className: classes.selected },
            !hideTags && renderTags(tags),
            React.createElement("span", { ref: suggestionsContainerRef },
                !hideAddButton && canUpdate && (!canAddOnlyOneTag || (canAddOnlyOneTag && tags.length < 1)) ? (React.createElement("div", { className: "".concat(classes.tagInput).concat(tiny ? ' tiny' : '') }, showAddNewTag ? (React.createElement("input", { type: "text", autoFocus: true, placeholder: placeholderString, "aria-label": placeholderString, onChange: handleChange, onKeyDown: handleKeyDown, onFocus: function (e) {
                        e.preventDefault();
                        onFocusHandler();
                    }, onBlur: resetState, value: query })) : customButton ? (customButton({ onClick: showTagInput })) : (React.createElement(TagAddButtonAndLabel, { className: afterButtonLabelType.toLowerCase() },
                    React.createElement("button", { onClick: showTagInput, type: "button", disabled: disabled, onMouseEnter: function () { return showHelper(); }, onMouseLeave: Manager.closePopover, ref: addTagRef }, customIcon ? customIcon : React.createElement(FaPlus, null)),
                    afterButtonLabel && tags.length < 1 ? (React.createElement("label", { onClick: showTagInput }, afterButtonLabel)) : null)))) : null,
                showingSuggestions ? (React.createElement(Suggestions, { suggestions: filteredSuggestions, selectedIndex: selectedIndex, handleClick: handleSuggestionClick, handleParentTagClick: handleParentTagClick, handleHover: handleSuggestionHover, handleParentTagBackClick: handleParentTagBackClick, classNames: classes, parent: parentTag ? parentTag.name : null, containerRef: suggestionsContainerRef })) : null))));
}
export default Tags;
